import { useEffect, useMemo } from "react"
import { throttle } from "lodash"

/**
 * Custom hook that throttles function calls to improve performance.
 *
 * @param callback - Function to throttle
 * @param delay - Time in milliseconds to throttle function calls (default: 200ms)
 * @returns Throttled version of callback that limits execution frequency
 *
 * @example
 * const handleScroll = useThrottle(() => {
 *   // Handle scroll event
 * }, 300);
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic throttle hook needs flexible parameter handling for various callback signatures
export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number = 200,
): T {
  const throttled = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Generic throttle hook needs flexible parameter handling for various callback signatures
    () => throttle((...args: any) => callback(...args), delay),
    [callback, delay],
  )

  useEffect(() => {
    return () => {
      throttled.cancel()
    }
  }, [throttled])

  return throttled as unknown as T
}
